import { useEffect } from 'react'
import { useRouterState } from '@tanstack/react-router'

const SITE_URL = 'https://technyc.io'
const INDEX_ROBOTS = 'index,follow,max-image-preview:large'
const NOINDEX_ROBOTS = 'noindex,nofollow'

type SeoConfig = {
  title: string
  description: string
  robots: string
  canonical: string | null
  socialUrl: string
}

const PUBLIC_ROUTE_META: Record<string, Pick<SeoConfig, 'title' | 'description'>> = {
  '/': {
    title: 'TechNYC.io - Learn AI & Systems Thinking',
    description:
      'Learn faster with AI-powered education. TechNYC.io offers courses in AI agents, systems thinking, data security, and modern technology skills.',
  },
  '/pricing': {
    title: 'Pricing | TechNYC.io',
    description:
      'Compare TechNYC.io plans and choose the membership that fits your AI, systems thinking, and hands-on learning goals.',
  },
  '/contact': {
    title: 'Contact | TechNYC.io',
    description:
      'Contact TechNYC.io to talk about memberships, team training, or how we can help your team learn practical AI and systems skills.',
  },
}

function normalizePathname(pathname: string) {
  if (!pathname || pathname === '/') {
    return '/'
  }

  return pathname.endsWith('/') ? pathname.slice(0, -1) || '/' : pathname
}

function buildAbsoluteUrl(pathname: string) {
  return pathname === '/' ? `${SITE_URL}/` : `${SITE_URL}${pathname}`
}

function buildNoindexSeo(pathname: string, title: string, description: string): SeoConfig {
  const normalizedPath = normalizePathname(pathname)

  return {
    title,
    description,
    robots: NOINDEX_ROBOTS,
    canonical: buildAbsoluteUrl(normalizedPath),
    socialUrl: buildAbsoluteUrl(normalizedPath),
  }
}

function getSeoForPath(pathname: string): SeoConfig {
  const normalizedPath = normalizePathname(pathname)
  const publicRouteMeta = PUBLIC_ROUTE_META[normalizedPath]

  if (publicRouteMeta) {
    return {
      ...publicRouteMeta,
      robots: INDEX_ROBOTS,
      canonical: buildAbsoluteUrl(normalizedPath),
      socialUrl: buildAbsoluteUrl(normalizedPath),
    }
  }

  if (normalizedPath === '/signin') {
    return buildNoindexSeo(
      normalizedPath,
      'Sign in | TechNYC.io',
      'Sign in to access your TechNYC.io courses, labs, and account.'
    )
  }

  if (normalizedPath.startsWith('/auth/')) {
    return buildNoindexSeo(
      normalizedPath,
      'Signing in | TechNYC.io',
      'Completing authentication for your TechNYC.io account.'
    )
  }

  if (normalizedPath === '/checkout') {
    return buildNoindexSeo(
      normalizedPath,
      'Checkout | TechNYC.io',
      'Secure checkout for your TechNYC.io membership.'
    )
  }

  if (normalizedPath === '/dashboard') {
    return buildNoindexSeo(
      normalizedPath,
      'Dashboard | TechNYC.io',
      'View your learning progress, challenges, and subscription details on TechNYC.io.'
    )
  }

  if (normalizedPath === '/suggestions') {
    return buildNoindexSeo(
      normalizedPath,
      'Suggestions | TechNYC.io',
      'Share product feedback and course suggestions with the TechNYC.io team.'
    )
  }

  if (normalizedPath === '/skill-tree-concept') {
    return buildNoindexSeo(
      normalizedPath,
      'Skill Tree Concept | TechNYC.io',
      'Internal concept view for TechNYC.io learning paths.'
    )
  }

  if (normalizedPath === '/courses' || normalizedPath.startsWith('/courses/')) {
    return buildNoindexSeo(
      normalizedPath,
      'Courses | TechNYC.io',
      'Browse and continue your TechNYC.io courses and lessons.'
    )
  }

  if (normalizedPath === '/labs' || normalizedPath.startsWith('/labs/')) {
    return buildNoindexSeo(
      normalizedPath,
      'Labs | TechNYC.io',
      'Work through hands-on TechNYC.io labs and interactive exercises.'
    )
  }

  if (normalizedPath.startsWith('/welcome/')) {
    return buildNoindexSeo(
      normalizedPath,
      'Course Preview | TechNYC.io',
      'Preview the TechNYC.io learning experience before you sign up.'
    )
  }

  if (normalizedPath.startsWith('/onboarding/')) {
    return buildNoindexSeo(
      normalizedPath,
      'Onboarding | TechNYC.io',
      'Complete your TechNYC.io onboarding to personalize your learning path.'
    )
  }

  return {
    title: 'Page Not Found | TechNYC.io',
    description: 'This page could not be found on TechNYC.io.',
    robots: NOINDEX_ROBOTS,
    canonical: null,
    socialUrl: buildAbsoluteUrl(normalizedPath),
  }
}

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  let meta = document.head.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null

  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute(attribute, key)
    document.head.appendChild(meta)
  }

  meta.setAttribute('content', content)
}

function upsertCanonical(canonical: string | null) {
  const existingCanonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null

  if (!canonical) {
    existingCanonical?.remove()
    return
  }

  if (existingCanonical) {
    existingCanonical.setAttribute('href', canonical)
    return
  }

  const canonicalLink = document.createElement('link')
  canonicalLink.setAttribute('rel', 'canonical')
  canonicalLink.setAttribute('href', canonical)
  document.head.appendChild(canonicalLink)
}

export default function RouteSeo() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const seo = getSeoForPath(pathname)

    document.title = seo.title

    upsertMeta('name', 'description', seo.description)
    upsertMeta('name', 'robots', seo.robots)
    upsertMeta('property', 'og:title', seo.title)
    upsertMeta('property', 'og:description', seo.description)
    upsertMeta('property', 'og:url', seo.socialUrl)
    upsertMeta('property', 'twitter:title', seo.title)
    upsertMeta('property', 'twitter:description', seo.description)
    upsertMeta('property', 'twitter:url', seo.socialUrl)
    upsertCanonical(seo.canonical)
  }, [pathname])

  return null
}
